// hooks/auth/useRecoverySetPassword.ts
import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAuthStatus,
  selectAuthError,
  selectFieldErrors,
  selectSetPasswordStage,
} from '@/store/selectors/auth';
import { recoverPasswordThunk } from '@/store/thunks/auth/recoverPasswordThunk';
import { clearLoginError } from '@/store/slices/auth/authSlice';

/**
 * Hook for /reset-password/set-password route
 * Handles new password submission during password recovery
 */
export function useRecoverySetPassword() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authStatus = useAppSelector(selectAuthStatus);
  const error = useAppSelector(selectAuthError);
  const fieldErrors = useAppSelector(selectFieldErrors);
  const stage = useAppSelector(selectSetPasswordStage);
  const isLoading = authStatus === 'loading';

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  const submit = async(password: string) => {
    const action = await dispatch(recoverPasswordThunk({ password }));
    if (recoverPasswordThunk.fulfilled.match(action)) {
      await navigate({ to: '/reset-password/success' });
    }
  };

  const back = async() => {
    await navigate({ to: '/reset-password/two-factor-verify' });
  };

  return {
    state: { isLoading, error, fieldErrors, stage },
    handlers: { submit, back },
  };
}
